import { Navigate, Outlet, useLocation } from "react-router-dom";
import "./App.css";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  auth,
  getTokenLocal,
  onMessageListener,
} from "../config/firebaseConfig";
import Hotjar from "@hotjar/browser";
import { useEffect, useState } from "react";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "@/components/app-sidebar";
import { SidebarCollapseForRoute } from "@/components/SidebarCollapseForRoute";
import AppHeader from "@/components/app-header";
import { AppSessionBanners } from "@/components/app-session-banners";
import { Toaster } from "@/components/ui/toaster";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { PulseCheckInBanner } from "@/components/PulseCheckInBanner";
import { Loader2 } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { ProductScopeProvider } from "@/context/ProductScopeContext";
import { cn } from "@/lib/utils";
import {
  hasProductAccess,
  hasProductAccessWithFallback,
  isAccountExpired,
} from "@/utils/productAccess";
import { CoachRail } from "@/components/coach/CoachRail";
import { CommandPalette } from "@/components/coach/CommandPalette";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { SetupReminderModal } from "@/components/setup-reminders/SetupReminderModal";
import { IdleLogoutManager } from "@/components/idle-logout/IdleLogoutManager";

const HOTJAR_SITE_ID = 3676943;
const HOTJAR_VERSION = 6;

const AppLayout = () => {
  const location = useLocation();
  // The merged /candidates workspace owns its own full-height chrome (no card
  // wrapper, own scroll). Legacy /chat and /matches URLs redirect into it.
  const isChatRoute =
    location.pathname === "/recruit/candidates" ||
    location.pathname.startsWith("/recruit/candidates/");
  // Dashboard (/) and every Coach page (/coach, /coach/*) are full-width
  // inside the card: their headers carry their own insets, so no max-width
  // gutter. The /coach chat route owns its own scroll so it gets a full
  // flex-height container.
  const isIntelligenceRoute =
    location.pathname === "/" ||
    location.pathname === "/coach" ||
    location.pathname.startsWith("/coach/");
  const isIntelligenceChatRoute = location.pathname === "/coach";
  const [user, loading] = useAuthState(auth);
  const [_isTokenFound, setTokenFound] = useState(false);
  const { businessProfile, loading: profileLoading } = useProfile();

  Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION);

  useEffect(() => {
    const init = async () => {
      if (user) {
        await getTokenLocal((success) => {
          setTokenFound(success);
        });

        onMessageListener()
          .then(() => {})
          .catch((err) =>
            console.error("Error receiving foreground message:", err),
          );
      }
    };

    init();
  }, [user]);

  /*   useEffect(() => {
    if (user && !loading) {
      // Brevo Conversations script
      (function (d, w, c) {
        w.BrevoConversationsID = "68da704425455a9a470ed82c";
        w[c] =
          w[c] ||
          function () {
            // eslint-disable-next-line prefer-rest-params -- Brevo widget API expects arguments
            (w[c].q = w[c].q || []).push(arguments);
          };
        // eslint-disable-next-line no-var
        var s = d.createElement("script");
        s.async = true;
        s.src = "https://conversations-widget.brevo.com/brevo-conversations.js";
        if (d.head) d.head.appendChild(s);
      })(document, window as any, "BrevoConversations");

      return () => {
        const script = document.querySelector(
          'script[src="https://conversations-widget.brevo.com/brevo-conversations.js"]',
        );
        if (script) {
          script.remove();
        }
        delete (window as any).BrevoConversations;
        delete (window as any).BrevoConversationsID;
      };
    }
  }, [user, loading]); */

  if (!user && !loading) {
    return <Navigate to="/sign-in" />;
  }

  if (loading || (user && profileLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/graftus_logo.jpg" className="h-10" alt="logo" />
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (user && !businessProfile) {
    return <Navigate to="/onboarding" replace />;
  }

  // "No product at all" means the business never finished onboarding. An
  // expired account is a different case — it had products and its time limit
  // ran out — so it stays here and lands on the dashboard, which explains what
  // happened, rather than being sent back through onboarding.
  if (user && businessProfile && !isAccountExpired(businessProfile, Date.now())) {
    const hasAnyProduct =
      hasProductAccessWithFallback(businessProfile, "recruit", Date.now()) ||
      hasProductAccessWithFallback(businessProfile, "leads", Date.now()) ||
      !!businessProfile.interestedProducts?.length ||
      hasProductAccess(businessProfile, "intelligence", Date.now());
    if (!hasAnyProduct) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return (
    <ProductScopeProvider>
      <SidebarProvider
        defaultOpen={true}
        className="flex h-dvh max-h-dvh min-h-0 w-full overflow-hidden"
      >
        <IdleLogoutManager />
        <SidebarCollapseForRoute />
        <AppSidebar className="border-none" />
        <SidebarInset className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="sticky top-0 z-50 flex shrink-0 flex-col bg-background">
            <AppSessionBanners />
            <div className="lg:hidden">
              <AppHeader />
            </div>
          </div>
          <div className="shrink-0">
            <SubscriptionBanner />
            <PulseCheckInBanner />
          </div>
          <div
            className={cn(
              "min-h-0 min-w-0 flex-1",
              isChatRoute
                ? "flex min-h-0 flex-col overflow-hidden p-0 justify-center"
                : isIntelligenceChatRoute
                  ? "flex min-h-0 flex-col overflow-hidden pr-2 sm:py-4"
                  : "app-content-scroll overflow-y-auto overflow-x-hidden pr-2 sm:py-4",
            )}
          >
            {isChatRoute ? (
              <Outlet />
            ) : (
              <div
                className={cn(
                  "bg-card w-full rounded-xl border border-border py-4",
                  isIntelligenceChatRoute
                    ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                    : "min-h-full",
                )}
              >
                <div
                  className={cn(
                    "mx-auto flex w-full min-w-0 flex-col",
                    isIntelligenceRoute ? "max-w-none" : "max-w-6xl px-4",
                    isIntelligenceChatRoute && "min-h-0 flex-1",
                  )}
                >
                  <Outlet />
                </div>
              </div>
            )}
            <Toaster />
          </div>
          <MobileBottomNav />
          <SetupReminderModal />
        </SidebarInset>
        <CoachRail />
        <CommandPalette />
      </SidebarProvider>
    </ProductScopeProvider>
  );
};

export default AppLayout;
